// Standalone manual units only. This module does not authorize delivery.
// Callers must supply a fresh first-party AND approved Google consent check.
export const ADSENSE_SCRIPT_ORIGIN = "https://pagead2.googlesyndication.com";
const attempts = new Set();
let loader;
let publisher;

export function validateAdSenseUnit({ publisherId, slotId }) {
  if (!/^ca-pub-\d{16}$/.test(publisherId || "") || !/^\d{1,30}$/.test(slotId || "")) {
    throw new Error("ADSENSE_CONFIGURATION_INVALID");
  }
}

export function loadAdSense(publisherId) {
  validateAdSenseUnit({ publisherId, slotId: "1" });
  if (publisher && publisher !== publisherId) return Promise.reject(new Error("ADSENSE_PUBLISHER_CONFLICT"));
  if (loader) return loader;
  publisher = publisherId;
  loader = new Promise((resolve, reject) => {
    // An unmanaged script could enable Auto Ads or use a different publisher.
    // Refuse coexistence rather than guessing who owns it.
    if ([...document.scripts].some(node => /\/adsbygoogle\.js(?:\?|$)/.test(node.src))) {
      reject(new Error("ADSENSE_UNMANAGED_SCRIPT"));
      return;
    }
    const script = document.createElement("script");
    script.async = true;
    script.crossOrigin = "anonymous";
    script.src = `${ADSENSE_SCRIPT_ORIGIN}/pagead/js/adsbygoogle.js?client=${publisherId}`;
    let finished = false;
    const finish = error => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      script.onload = null;
      script.onerror = null;
      if (error) reject(new Error(error)); else resolve();
    };
    const timer = setTimeout(() => finish("ADSENSE_LOADER_TIMEOUT"), 12000);
    script.onload = () => finish();
    script.onerror = () => finish("ADSENSE_LOADER_FAILED");
    document.head.appendChild(script);
  });
  return loader;
}

export function startAdSenseUnit({ container, publicationId, placement, publisherId, slotId,
  canRequest, onState = () => {}, timeoutMs = 15000, personalized = false }) {
  validateAdSenseUnit({ publisherId, slotId });
  if (!publicationId || !placement || typeof canRequest !== "function") throw new Error("ADSENSE_CONFIGURATION_INVALID");
  const key = JSON.stringify([publicationId, placement]);
  let stopped = false;
  let observer;
  let timer;
  let unit;
  let filled = false;
  const permitted = () => { try { return canRequest() === true; } catch { return false; } };
  const stop = code => {
    if (stopped) return;
    stopped = true;
    clearTimeout(timer);
    observer?.disconnect();
    window.removeEventListener("surgedome:consent-changed", consentChanged);
    unit?.remove();
    if (code) onState(code);
  };
  const consentChanged = () => { if (!permitted()) stop("CONSENT_WITHDRAWN"); };
  if (!permitted()) { stop("CONSENT_REQUIRED"); return stop; }
  if (attempts.has(key)) { stop("ADSENSE_DUPLICATE_SUPPRESSED"); return stop; }
  if (!container?.isConnected || container.clientWidth <= 0) { stop("ADSENSE_WIDTH_UNAVAILABLE"); return stop; }
  window.addEventListener("surgedome:consent-changed", consentChanged);
  onState("ADSENSE_LOADING");
  timer = setTimeout(() => stop("ADSENSE_TIMEOUT"), timeoutMs);
  loadAdSense(publisherId).then(() => {
    if (stopped) return;
    if (!permitted()) { stop("CONSENT_WITHDRAWN"); return; }
    if (!container.isConnected || container.clientWidth <= 0) { stop("ADSENSE_WIDTH_UNAVAILABLE"); return; }
    // Recheck after the shared asynchronous loader: two mounts may have raced.
    if (attempts.has(key)) { stop("ADSENSE_DUPLICATE_SUPPRESSED"); return; }
    unit = document.createElement("ins");
    unit.className = "adsbygoogle";
    unit.style.display = "block";
    unit.setAttribute("data-ad-client", publisherId);
    unit.setAttribute("data-ad-slot", slotId);
    unit.setAttribute("data-ad-format", "auto");
    unit.setAttribute("data-full-width-responsive", "true");
    observer = new MutationObserver(() => {
      if (stopped) return;
      if (!permitted()) { stop("CONSENT_WITHDRAWN"); return; }
      const status = unit.getAttribute("data-ad-status");
      if (status === "unfilled") stop("ADSENSE_UNFILLED");
      else if (status === "filled" && !filled) {
        filled = true;
        clearTimeout(timer);
        onState("ADSENSE_FILLED");
      }
    });
    observer.observe(unit, { attributes: true, attributeFilter: ["data-ad-status"] });
    container.appendChild(unit);
    attempts.add(key);
    window.adsbygoogle = window.adsbygoogle || [];
    // Non-personalized unless the caller passes the reader's current personalization consent
    // (AdSense direct only; the reviewed per-placement path never does).
    window.adsbygoogle.requestNonPersonalizedAds = personalized === true ? 0 : 1;
    window.adsbygoogle.push({});
    onState("ADSENSE_REQUEST_INVOKED");
  }).catch(error => stop(/^ADSENSE_[A-Z_]+$/.test(error.message) ? error.message : "ADSENSE_RENDER_FAILED"));
  // No automatic refresh, external fallback or retry. A sent request cannot be
  // recalled. Its document-level claim survives teardown/withdrawal/remount.
  return () => stop();
}
